import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  FiArrowRight,
  FiMapPin,
  FiUpload,
  FiTrendingDown,
  FiX,
} from "react-icons/fi";
import { colors, gradients, radii, shadows, animations } from "./designSystem";

const STEPS = [
  {
    icon: FiMapPin,
    eyebrow: "Step 1 of 3",
    title: "Set your delivery location",
    subtitle:
      "We use this to find the closest licensed pharmacies and give you accurate delivery windows.",
    cta: "Continue",
    input: true,
  },
  {
    icon: FiUpload,
    eyebrow: "Step 2 of 3",
    title: "Upload a prescription or search medicines",
    subtitle:
      "Our AI reads your Rx in 3 seconds and auto-fills your medicine list. Or just search for what you need.",
    cta: "Continue",
  },
  {
    icon: FiTrendingDown,
    eyebrow: "Step 3 of 3",
    title: "Compare prices from nearby pharmacies",
    subtitle:
      "Real-time quotes from verified pharmacies near you. Pick the best price or the fastest delivery — you're in control.",
    cta: "Start exploring",
  },
];

const OnboardingOverlay = ({ onDismiss, onUploadRx }) => {
  const [step, setStep] = useState(0);
  const [loc, setLoc] = useState(
    typeof window !== "undefined" ? localStorage.getItem("pillstackLocation") || "" : "",
  );
  const current = STEPS[step];

  const next = () => {
    if (step === 0 && loc.trim()) {
      localStorage.setItem("pillstackLocation", loc.trim());
    }
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      onDismiss?.();
    }
  };

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex={2000}
      bg="rgba(15,23,42,0.55)"
      backdropFilter="blur(4px)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
      style={{ animation: "pillstack-fade-in 0.3s ease-out" }}
    >
      <style>{animations}</style>
      <Box
        bg="white"
        borderRadius={radii.xl}
        boxShadow={shadows.xl}
        maxW="520px"
        w="100%"
        overflow="hidden"
        position="relative"
      >
        <IconButton
          aria-label="Skip onboarding"
          icon={<FiX />}
          size="sm"
          variant="ghost"
          position="absolute"
          top={3}
          right={3}
          color={colors.neutral[500]}
          onClick={onDismiss}
          _hover={{ bg: colors.neutral[50], color: colors.neutral[900] }}
        />
        <Box
          bgGradient={gradients.brand}
          h="140px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            inset={0}
            backgroundImage="radial-gradient(circle at 25% 25%, rgba(255,255,255,0.22), transparent 55%)"
          />
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            bg="rgba(255,255,255,0.18)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            zIndex={1}
          >
            <Icon as={current.icon} boxSize={9} />
          </Box>
        </Box>

        <Box p={{ base: 6, md: 8 }}>
          <Text fontSize="11px" fontWeight="800" color={colors.brand[600]} textTransform="uppercase" letterSpacing="0.12em" m={0}>
            {current.eyebrow}
          </Text>
          <Text fontSize="24px" fontWeight="800" color={colors.neutral[900]} m={0} mt={2} letterSpacing="-0.02em">
            {current.title}
          </Text>
          <Text fontSize="14px" color={colors.neutral[500]} m={0} mt={3} lineHeight="1.65">
            {current.subtitle}
          </Text>

          {current.input ? (
            <Box mt={5}>
              <Input
                placeholder="Area or city (e.g. Sector 19, Dwarka)"
                value={loc}
                onChange={(e) => setLoc(e.target.value)}
                borderColor={colors.neutral[200]}
                _focus={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
              />
              <Text fontSize="11px" color={colors.neutral[400]} mt={1.5} m={0}>
                You can change this anytime from the top bar.
              </Text>
            </Box>
          ) : null}

          <HStack spacing={2} mt={6} justify="center">
            {STEPS.map((_, i) => (
              <Box
                key={i}
                w={i === step ? "28px" : "8px"}
                h="8px"
                borderRadius="full"
                bg={i === step ? colors.brand[500] : colors.neutral[200]}
                transition="all 0.3s ease"
              />
            ))}
          </HStack>

          <Flex mt={6} justify="space-between" align="center">
            <Button variant="ghost" color={colors.neutral[500]} onClick={onDismiss} _hover={{ color: colors.neutral[900] }}>
              Skip tour
            </Button>
            <HStack spacing={2}>
              {step > 0 ? (
                <Button variant="outline" borderColor={colors.neutral[200]} color={colors.neutral[700]} onClick={() => setStep((s) => Math.max(0, s - 1))}>
                  Back
                </Button>
              ) : null}
              <Button
                bgGradient={gradients.brand}
                color="white"
                _hover={{ bgGradient: gradients.brandBold, boxShadow: shadows.brandHover }}
                rightIcon={<FiArrowRight />}
                onClick={() => {
                  if (step === STEPS.length - 1 && onUploadRx) onUploadRx();
                  next();
                }}
                fontWeight="800"
              >
                {current.cta}
              </Button>
            </HStack>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingOverlay;
